/**
 * Lookup hook for the Hired Equipment submission grid.
 *
 * Vendors = active Vendor rows (Module B), optionally filtered by service area.
 * Resources = that vendor's VendorResource catalogue (with live approved rates),
 * plus PO line items when a PO is selected (catalogue units not on the PO stay
 * selectable).
 * POs = ACTIVE purchase orders for the vendor (Module A); line items (with
 * per-line tasks) come from GET /purchase-orders/:id.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listHiredEquipmentFuelSurcharges } from '../../../services/hiredEquipmentService';
import { hiredEquipmentPoService } from '../../../services/hiredEquipmentPoService';
import { taskService } from '../../../services/taskService';
import { vendorService } from '../../../services/vendorService';
import { heExcludedStandingTypes } from '../types';
import type {
  FuelSurchargeLookupRow,
  HiredEquipmentAnchor,
  HiredEquipmentGridRow,
  PurchaseOrderOption,
  ResourceOption,
  TaskOption,
  VendorOption,
} from '../types';
import { mergePoAndVendorResources, vendorResourcesToResourceOptions } from '../utils/masterResources';
import { formatTaskLabel, poGridResourceId, resolveLockedPoId, toTaskOption } from '../utils/poPrefill';
import { mergeTaskOptions, uniqueTasksFromResourceOptions } from '../utils/poTaskFilter';

function uniqueNumbers(values: Array<number | null | undefined>): number[] {
  return [...new Set(values.filter((id): id is number => id != null))];
}

function idsFromKey(key: string): number[] {
  return key ? key.split(',').map(Number) : [];
}

export function useHiredEquipmentGridData(
  anchor: HiredEquipmentAnchor,
  rows: HiredEquipmentGridRow[],
  options: {
    /** C2: offer Standing Routine tasks (the user can create the Specific task from one). */
    standingRoutineSelectable?: boolean;
  } = {},
) {
  const standingRoutineSelectable = options.standingRoutineSelectable === true;
  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [tasksById, setTasksById] = useState<Map<number, TaskOption>>(new Map());
  const [posByVendorId, setPosByVendorId] = useState<Record<number, PurchaseOrderOption[]>>({});
  // Straight-by-id PO lookups (number/description/vendor), independent of `posByVendorId`'s
  // ACTIVE-for-vendor filter. A row or the anchor can carry a PO that's no longer ACTIVE (e.g.
  // fully invoiced) — that PO would never appear in `posByVendorId` and so would never get a
  // description anywhere in the UI without this.
  const [posById, setPosById] = useState<Record<number, PurchaseOrderOption>>({});
  const [resourcesByPoId, setResourcesByPoId] = useState<Record<number, ResourceOption[]>>({});
  const [tasksByPoId, setTasksByPoId] = useState<Record<number, TaskOption[]>>({});
  const [masterByVendorId, setMasterByVendorId] = useState<Record<number, ResourceOption[]>>({});
  const [fuelSurcharges, setFuelSurcharges] = useState<FuelSurchargeLookupRow[]>([]);

  const posLoadedRef = useRef<Set<number>>(new Set());
  const poResourcesLoadedRef = useRef<Set<number>>(new Set());
  const masterLoadedRef = useRef<Set<number>>(new Set());
  const tasksLoadedRef = useRef<Set<number>>(new Set());
  // Mirrors `tasksById` so a callback can read the current cache without taking it as a dependency
  // and being rebuilt on every hydration.
  const tasksByIdRef = useRef<Map<number, TaskOption>>(new Map());
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    posLoadedRef.current = new Set();
    masterLoadedRef.current = new Set();
    setPosByVendorId({});
    setMasterByVendorId({});
  }, [anchor.serviceAreaId]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const vendorRows = await vendorService.getAllVendors({
          status: 'ACTIVE',
          ...(anchor.serviceAreaId != null ? { serviceAreaId: anchor.serviceAreaId } : {}),
        });
        if (cancelled) return;
        setVendors(vendorRows.map((row) => ({ id: row.id, name: row.name })));
      } catch (error) {
        console.error('Failed to load hired equipment grid lookups', error);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [anchor.serviceAreaId]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await listHiredEquipmentFuelSurcharges();
        if (cancelled) return;
        setFuelSurcharges(data);
      } catch (error) {
        console.error('Failed to load hired equipment fuel surcharges', error);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // M18: the id lists below are keyed by their CONTENT, not rebuilt per keystroke. They feed the
  // lookup effects, and a new array identity on every edit used to re-run each effect, cancel the
  // request in flight and start it again – the PO picker refetched ~20 times while editing.
  const vendorIdsKey = uniqueNumbers([anchor.lockedVendorId, ...rows.map((row) => row.vendorId)]).join(',');
  const vendorIds = useMemo(() => idsFromKey(vendorIdsKey), [vendorIdsKey]);

  const purchaseOrders = useMemo(() => {
    const fromVendors = vendorIds.flatMap((id) => posByVendorId[id] ?? []);
    const seen = new Set(fromVendors.map((po) => po.id));
    const fromDirectLookup = Object.values(posById).filter((po) => !seen.has(po.id));
    return [...fromVendors, ...fromDirectLookup];
  }, [vendorIds, posByVendorId, posById]);

  const effectiveLockedPoId = useMemo(
    () => resolveLockedPoId(anchor, rows, purchaseOrders),
    [anchor, rows, purchaseOrders],
  );

  const poIdsKey = uniqueNumbers([
    effectiveLockedPoId,
    anchor.lockedPoId,
    ...rows.map((row) => row.poId),
    ...purchaseOrders.map((po) => po.id),
  ]).join(',');
  const poIds = useMemo(() => idsFromKey(poIdsKey), [poIdsKey]);

  const taskIdsKey = uniqueNumbers([anchor.lockedTaskId, ...rows.map((row) => row.taskId)]).join(',');
  const taskIds = useMemo(() => idsFromKey(taskIdsKey), [taskIdsKey]);

  useEffect(() => {
    const missing = vendorIds.filter((id) => !posLoadedRef.current.has(id));
    if (missing.length === 0) return;
    // Marked before the request, so a re-run while it is in flight does not fetch again.
    for (const id of missing) posLoadedRef.current.add(id);

    const load = async () => {
      const entries = await Promise.all(
        missing.map(async (vendorId) => {
          const pos = await hiredEquipmentPoService.getActivePosForVendor(vendorId, anchor.serviceAreaId);
          return [
            vendorId,
            pos.map((po) => ({
              id: po.id,
              poNumber: po.poNumber,
              vendorId: po.vendorId,
              description: po.description ?? null,
            })),
          ] as const;
        }),
      );
      if (!mountedRef.current) return;
      setPosByVendorId((prev) => {
        const next = { ...prev };
        for (const [vendorId, pos] of entries) next[vendorId] = pos;
        return next;
      });
    };
    load().catch((error) => {
      console.error('Hired equipment lookup failed', error);
      for (const id of missing) posLoadedRef.current.delete(id);
    });
    return () => {
      // In-flight lookups are kept: their ids are already marked as loading.
    };
  }, [vendorIds, anchor.serviceAreaId]);

  useEffect(() => {
    const missing = poIds.filter((id) => !poResourcesLoadedRef.current.has(id));
    if (missing.length === 0) return;
    // Marked before the request, so a re-run while it is in flight does not fetch again.
    for (const id of missing) poResourcesLoadedRef.current.add(id);

    const load = async () => {
      const entries = await Promise.all(
        missing.map(async (poId) => {
          const { resources, tasks, po } = await hiredEquipmentPoService.loadPo(poId);
          const options: ResourceOption[] = resources.map((resource) => ({
            id: poGridResourceId(resource),
            label: resource.description,
            rate: resource.rate,
            unit: resource.unitOfMeasurement,
            vehicleType: resource.vehicleType ?? null,
            overtimeEligible: resource.overtimeEligible === true,
            catalogueId: resource.equipmentMasterId ?? null,
            taskId: resource.taskId,
            taskLabel:
              resource.taskLabel
              ?? (resource.taskId
                ? formatTaskLabel({
                    id: resource.taskId,
                    taskNumber: resource.taskNumber,
                    taskDescription: resource.taskDescription,
                  })
                : null),
          }));
          return [poId, options, tasks, po] as const;
        }),
      );
      if (!mountedRef.current) return;
      setResourcesByPoId((prev) => {
        const next = { ...prev };
        for (const [poId, options] of entries) next[poId] = options;
        return next;
      });
      setTasksByPoId((prev) => {
        const next = { ...prev };
        for (const [poId, , tasks] of entries) next[poId] = tasks;
        return next;
      });
      setPosById((prev) => {
        const next = { ...prev };
        for (const [poId, , , po] of entries) {
          if (po) {
            next[poId] = {
              id: po.id,
              poNumber: po.poNumber,
              vendorId: po.vendorId ?? 0,
              description: po.description,
            };
          }
        }
        return next;
      });
    };
    load().catch((error) => {
      console.error('Hired equipment lookup failed', error);
      for (const id of missing) poResourcesLoadedRef.current.delete(id);
    });
    return () => {
      // In-flight lookups are kept: their ids are already marked as loading.
    };
  }, [poIds]);

  useEffect(() => {
    const missing = vendorIds.filter((id) => !masterLoadedRef.current.has(id));
    if (missing.length === 0) return;
    // Marked before the request, so a re-run while it is in flight does not fetch again.
    for (const id of missing) masterLoadedRef.current.add(id);

    const load = async () => {
      const entries = await Promise.all(
        missing.map(async (vendorId) => {
          try {
            const vendor = await vendorService.getVendorById(vendorId);
            return [
              vendorId,
              vendorResourcesToResourceOptions(vendor.resources ?? [], anchor.serviceAreaId),
            ] as const;
          } catch {
            return [vendorId, [] as ResourceOption[]] as const;
          }
        }),
      );
      if (!mountedRef.current) return;
      setMasterByVendorId((prev) => {
        const next = { ...prev };
        for (const [vendorId, options] of entries) next[vendorId] = options;
        return next;
      });
    };
    load().catch((error) => {
      console.error('Hired equipment lookup failed', error);
      for (const id of missing) masterLoadedRef.current.delete(id);
    });
    return () => {
      // In-flight lookups are kept: their ids are already marked as loading.
    };
  }, [vendorIds, anchor.serviceAreaId]);

  useEffect(() => {
    tasksByIdRef.current = tasksById;
  }, [tasksById]);

  useEffect(() => {
    const missing = taskIds.filter((id) => !tasksLoadedRef.current.has(id));
    if (missing.length === 0) return;
    // Marked before the request, so a re-run while it is in flight does not fetch again.
    for (const id of missing) tasksLoadedRef.current.add(id);

    const load = async () => {
      try {
        const taskRows = await taskService.getLightTasksByIds(missing);
        if (!mountedRef.current) return;
        setTasksById((prev) => {
          const next = new Map(prev);
          for (const task of taskRows) {
            next.set(task.id, toTaskOption(task));
          }
          return next;
        });
      } catch (error) {
        console.error('Failed to load task labels for hired equipment grid', error);
        for (const id of missing) tasksLoadedRef.current.delete(id);
      }
    };
    load();
    return () => {
      // In-flight lookups are kept: their ids are already marked as loading.
    };
  }, [taskIds]);

  const searchTasks = useCallback(async (query: string): Promise<TaskOption[]> => {
    const tasks = await taskService.searchActiveTasks(query, 20, {
      excludeStandingTypes: heExcludedStandingTypes(standingRoutineSelectable),
      effortEligibleOnly: true,
    });
    const found = tasks.map(toTaskOption);
    setTasksById((prev) => {
      const next = new Map(prev);
      for (const option of found) next.set(option.id, option);
      return next;
    });
    return found;
  }, [standingRoutineSelectable]);

  /**
   * Prime the label cache with a task the user just created or edited, so the row
   * renders its number/description without waiting for a lookup round-trip.
   */
  const registerTask = useCallback((option: TaskOption) => {
    tasksLoadedRef.current.add(option.id);
    setTasksById((prev) => {
      const next = new Map(prev);
      next.set(option.id, option);
      return next;
    });
  }, []);

  /**
   * The task's type/mode, fetching once if the cache does not have them yet.
   *
   * The hydration effect above fills this in for every row eventually, but "eventually" is not
   * good enough for Duplicate — a click that lands first would otherwise read an option with no
   * `type` and quietly skip cloning.
   */
  const ensureTaskClassification = useCallback(
    async (taskId: number): Promise<TaskOption | null> => {
      const cached = tasksByIdRef.current.get(taskId);
      if (cached && cached.type != null) return cached;
      try {
        const [task] = await taskService.getLightTasksByIds([taskId]);
        if (!task) return cached ?? null;
        const option = toTaskOption(task);
        tasksLoadedRef.current.add(option.id);
        setTasksById((prev) => {
          const next = new Map(prev);
          next.set(option.id, option);
          return next;
        });
        return option;
      } catch (error) {
        console.error('Failed to classify task for hired equipment grid', error);
        return cached ?? null;
      }
    },
    [],
  );

  const resourcesForRow = useCallback(
    (row: HiredEquipmentGridRow): ResourceOption[] => {
      const vendorId = row.vendorId ?? anchor.lockedVendorId ?? null;
      const vendorResources = vendorId != null ? (masterByVendorId[vendorId] ?? []) : [];
      if (row.poId != null) {
        const poResources = resourcesByPoId[row.poId];
        if (poResources && poResources.length > 0) {
          return mergePoAndVendorResources(poResources, vendorResources);
        }
      }
      return vendorResources;
    },
    [resourcesByPoId, masterByVendorId, anchor.lockedVendorId],
  );

  const tasksWithRowLabels = useMemo(() => {
    const byId = new Map(tasksById);
    for (const row of rows) {
      if (row.taskId != null && !byId.has(row.taskId)) {
        byId.set(row.taskId, {
          id: row.taskId,
          description: row.taskLabel || `Task ${row.taskId}`,
          name: row.taskLabel || undefined,
        });
      }
    }
    if (anchor.lockedTaskId != null && !byId.has(anchor.lockedTaskId)) {
      byId.set(anchor.lockedTaskId, {
        id: anchor.lockedTaskId,
        description: anchor.lockedTaskLabel || `Task ${anchor.lockedTaskId}`,
        name: anchor.lockedTaskLabel || undefined,
      });
    }
    return byId;
  }, [tasksById, rows, anchor.lockedTaskId, anchor.lockedTaskLabel]);

  const taskOptionForRow = useCallback(
    (row: HiredEquipmentGridRow): TaskOption | null => {
      if (row.taskId == null) return null;
      return (
        tasksWithRowLabels.get(row.taskId)
        ?? {
          id: row.taskId,
          description: row.taskLabel || `Task ${row.taskId}`,
          name: row.taskLabel || undefined,
        }
      );
    },
    [tasksWithRowLabels],
  );

  const tasksByPoIdWithRows = useMemo(() => {
    const next: Record<number, TaskOption[]> = { ...tasksByPoId };
    // Only widen POs that already carry line-level tasks. Creating an entry for a PO that has none
    // would turn poCarriesTask's `'unknown'` — an unfetched PO, or a project-level one — into a
    // definitive answer, and nextRowAfterTaskChange would then release a perfectly valid PO.
    // Derived from the raw map, so the two loops below cannot bootstrap each other.
    const constrained = new Set(
      Object.entries(tasksByPoId)
        .filter(([, tasks]) => tasks.length > 0)
        .map(([poId]) => Number(poId)),
    );
    for (const [poId, resources] of Object.entries(resourcesByPoId)) {
      const id = Number(poId);
      if (!constrained.has(id)) continue;
      const fromResources = uniqueTasksFromResourceOptions(resources);
      if (fromResources.length === 0) continue;
      next[id] = mergeTaskOptions(next[id], fromResources);
    }
    for (const row of rows) {
      if (row.poId == null || row.taskId == null) continue;
      if (!constrained.has(row.poId)) continue;
      const existing = next[row.poId];
      if (existing.some((task) => task.id === row.taskId)) continue;
      const option =
        tasksWithRowLabels.get(row.taskId)
        ?? {
          id: row.taskId,
          description: row.taskLabel || `Task ${row.taskId}`,
          name: row.taskLabel || undefined,
        };
      next[row.poId] = [...existing, option];
    }
    return next;
  }, [tasksByPoId, resourcesByPoId, rows, tasksWithRowLabels]);

  return {
    vendors,
    tasksById: tasksWithRowLabels,
    taskOptionForRow,
    searchTasks,
    registerTask,
    ensureTaskClassification,
    purchaseOrders,
    tasksByPoId: tasksByPoIdWithRows,
    resourcesForRow,
    fuelSurcharges,
  };
}
